import { BrowserRouter } from 'react-router-dom';

import { AppErrorBoundary } from '@/components/AppErrorBoundary';
import { SplashScreen } from '@/components/SplashScreen';
import { OnboardingTourProvider } from '@/components/onboarding/OnboardingTourProvider';
import { Toaster } from '@/components/ui/sonner';
import { useTheme } from '@/hooks/useTheme';
import { PostHogConfig, PostHogProvider } from '@/lib/analytics';
import Router from '@/Router';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { shouldPersistQueryKey } from '@/lib/offline/persistedQueries';
import { Suspense, useEffect } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import AlertProvider from './AlertProvider';
import DialogProvider from './DialogProvider';
import HotkeyProvider from './HotkeyProvider';
import UserProvider from './UserProvider';

const options: Partial<PostHogConfig> = {
  api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST!,
  defaults: '2025-05-24',
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24 * 7, // 7 days — keep cache long enough to persist/restore
      networkMode: 'offlineFirst',     // try network, fall back to cached data offline
    },
  },
});

const persister = createSyncStoragePersister({ storage: window.localStorage, key: 'gis-rq-cache-v2' });

const persistOptions = {
  persister,
  maxAge: 1000 * 60 * 60 * 24 * 7, // 7 days
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { queryKey: readonly unknown[]; state: { status: string } }) =>
      query.state.status === 'success' && shouldPersistQueryKey(query.queryKey),
  },
};

const Providers = () => {
  const theme = useTheme();

  useEffect(() => {
    document.documentElement.className = theme;
  }, [theme]);
  return (
    <AppErrorBoundary>
      <Suspense fallback={<SplashScreen />}>
        <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={options}>
          <Toaster />
          <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
          <BrowserRouter>
            <HotkeyProvider>
              <DialogProvider>
                <AlertProvider>
                  <UserProvider>
                    <OnboardingTourProvider>
                      <GoogleReCaptchaProvider reCaptchaKey='6LfqbNYrAAAAABhTqAPnXrE9bf_HBHU8wtKO-Q_Y'>
                        <Router />
                      </GoogleReCaptchaProvider>
                    </OnboardingTourProvider>
                  </UserProvider>
                </AlertProvider>
              </DialogProvider>
            </HotkeyProvider>
          </BrowserRouter>
          </PersistQueryClientProvider>
        </PostHogProvider>
      </Suspense>
    </AppErrorBoundary>
  );
};

export default Providers;
